import { Injectable } from "@angular/core";
import {
  BackendArguments,
  BackendBody,
  CreateOrderRequest,
  CreateTripRequest,
  OrderResponse,
  OrdersForUserResponse,
  LastFulfilledOrderResponse,
  TripPaymentRequirementResponse,
  TripResponse,
  UpdateOrderRequest,
} from "../../shared/backend-api.types";
import { ApiService } from "../http/api.service";
import { apiPath, tripEndpoint } from "../../shared/api-path.const";
import { NewOrder, Order, OrderAddress, Preferences } from "./order.interface";
import { OrderStatus } from "./order-status.type";
import { ServiceType } from "../subscription/plan-service-type.enum";
import { parseFulfillmentDateTime } from "../utils/date.utils";

export type GetOrdersQueryStringArgs = BackendArguments["OrderController_getOrders"]["parameters"]["query"];

export type GetOrdersForUserQueryStringArgs =
  BackendArguments["OrderController_getOrdersForUser"]["parameters"]["query"];

type CreateOrderBody = Omit<CreateOrderRequest, "address"> & { address: CreateOrderRequest["address"] | undefined };
type CreateTripBody = Record<keyof CreateTripRequest, CreateOrderBody>;
type UpdateOrderBody = Omit<UpdateOrderRequest, "driverId"> & { driverId?: string | null };

export interface OrderPage {
  orders: Order[];
  totalCount: number;
}

export type ApiOrder = BackendBody["ApiOrder"];
type ApiOrderDatePage = BackendBody["OrderDataPage"];
export type ApiOrderStatus = BackendBody["ApiOrder"]["status"];
export type ApiOrderServiceType = BackendBody["ApiOrder"]["serviceType"];

function toOrder(response: OrderResponse): Order {
  return {
    id: response.orderId,
    address: response.address
      ? ({
          apartment: response.address.apartment,
          country: response.address.country,
          formatted_address: response.address.formattedAddress,
          lat: response.address.lat,
          lng: response.address.lng,
          name: response.address.name,
          placeId: response.address.placeId,
          state: response.address.state,
          street: response.address.street,
          zipCode: response.address.zipCode,
        } as OrderAddress)
      : undefined,
    baseLocation: response.baseLocation as Order["baseLocation"],
    contactName: response.contactName,
    createdAt: response.createdAt,
    deleteRequested: response.deleteRequested,
    driverAccepted: response.driverAccepted,
    driverId: response.driverId,
    fulfillmentDate: response.fulfillmentDate,
    fulfillmentTime: response.fulfillmentTime as Order["fulfillmentTime"],
    items: response.itemIds,
    nextFirstTimePickupStep: response.nextFirstTimePickupStep,
    nextLocalDeliveryStep: response.nextLocalDeliveryStep,
    nextLocalPickupStep: response.nextLocalPickupStep,
    phoneNumber: response.phoneNumber,
    preferences: response.preferences
      ? {
          accommodation: response.preferences.accommodation as Preferences["accommodation"],
          details: response.preferences.details,
          hotelName: response.preferences.hotelName,
          instruction: response.preferences.instructions as Preferences["instruction"],
          reservationDetails: response.preferences.reservationDetails,
          unitNumber: response.preferences.unitNumber,
        }
      : undefined,
    price: response.price,
    serviceType: response.serviceType as unknown as ServiceType,
    status: response.status as unknown as OrderStatus,
    subscriptionId: response.subscriptionId,
    userId: response.userId,
  } as Order;
}

@Injectable({
  providedIn: "root",
})
export class OrderApiService {
  constructor(private readonly apiService: ApiService) {}

  public getOrders(args: GetOrdersQueryStringArgs): Promise<ApiOrderDatePage> {
    return this.apiService.sendGetRequest<ApiOrderDatePage, GetOrdersQueryStringArgs>(apiPath.order, args);
  }

  public async getById(id: string): Promise<Order> {
    const response = await this.apiService.sendGetRequest<OrderResponse>(`${apiPath.order}/${id}`, undefined);
    return toOrder(response);
  }

  public async getByUserId(userId: string): Promise<Order[]> {
    const response = await this.apiService.sendGetRequest<OrdersForUserResponse>(
      `${apiPath.order}/user/${userId}`,
      undefined,
    );
    return response.orders.map(toOrder);
  }

  public async getPageByUserId(userId: string, args: GetOrdersForUserQueryStringArgs): Promise<OrderPage> {
    const response = await this.apiService.sendGetRequest<OrdersForUserResponse, GetOrdersForUserQueryStringArgs>(
      `${apiPath.order}/user/${userId}`,
      args,
    );

    return { orders: response.orders.map(toOrder), totalCount: response.totalCount };
  }

  public async getAllOrdersForUserIdThisSeason(userId: string): Promise<Order[]> {
    const response = await this.apiService.sendGetRequest<OrdersForUserResponse, { season: string }>(
      `${apiPath.order}/user/${userId}`,
      { season: "current" },
    );
    return response.orders.map(toOrder);
  }

  public async getLastFulfilledOrderForUserId(userId: string): Promise<Order | undefined> {
    const response = await this.apiService.sendGetRequest<LastFulfilledOrderResponse>(
      `${apiPath.order}/user/${userId}/last-fulfilled`,
      undefined,
    );
    return response.order ? toOrder(response.order) : undefined;
  }

  public async create(order: NewOrder): Promise<string> {
    const body = this.buildCreateOrderBody(order);
    const response = await this.apiService.sendPostRequest<typeof body, OrderResponse>(apiPath.order, body);
    return response.orderId;
  }

  public async createForUser(order: NewOrder, userId: string): Promise<string> {
    const body = { ...this.buildCreateOrderBody(order), baseLocation: order.baseLocation, userId };
    const response = await this.apiService.sendPostRequest<typeof body, OrderResponse>(`${apiPath.order}/admin`, body);
    return response.orderId;
  }

  public async createTrip(delivery: NewOrder, pickup: NewOrder): Promise<TripResponse> {
    return this.apiService.sendPostRequest<CreateTripBody, TripResponse>(
      apiPath.trip,
      this.buildCreateTripBody(delivery, pickup),
    );
  }

  public async getTripPaymentRequirement(
    delivery: NewOrder,
    pickup: NewOrder,
  ): Promise<TripPaymentRequirementResponse> {
    return this.apiService.sendPostRequest<CreateTripBody, TripPaymentRequirementResponse>(
      `${apiPath.trip}/${tripEndpoint.paymentRequirement}`,
      this.buildCreateTripBody(delivery, pickup),
    );
  }

  private buildCreateTripBody(delivery: NewOrder, pickup: NewOrder): CreateTripBody {
    return { delivery: this.buildCreateOrderBody(delivery), pickup: this.buildCreateOrderBody(pickup) };
  }

  private buildCreateOrderBody(order: NewOrder): CreateOrderBody {
    const { earliest, latest } = parseFulfillmentDateTime(order.fulfillmentDate, order.fulfillmentTime);

    return {
      address: order.address
        ? {
            apartment: order.address.apartment,
            country: order.address.country,
            formattedAddress: order.address.formatted_address,
            lat: order.address.lat?.toString(),
            lng: order.address.lng?.toString(),
            name: order.address.name,
            placeId: order.address.placeId,
            state: order.address.state,
            street: order.address.street,
            zipCode: order.address.zipCode,
          }
        : undefined,
      earliestFulfillment: earliest,
      latestFulfillment: latest,
      itemIds: order.items ?? [],
      serviceType: order.serviceType,
      preferences: order.preferences
        ? {
            accommodation: order.preferences.accommodation,
            details: order.preferences.details,
            hotelName: order.preferences.hotelName,
            instructions: order.preferences.instruction,
            reservationDetails: order.preferences.reservationDetails,
            unitNumber: order.preferences.unitNumber,
          }
        : undefined,
    };
  }

  public async update(id: string, updates: Partial<Order>): Promise<void> {
    const body: UpdateOrderBody = {};
    if (updates.status !== undefined) body.status = updates.status as unknown as UpdateOrderRequest["status"];
    if (updates.driverId !== undefined) body.driverId = updates.driverId;
    if (updates.driverAccepted !== undefined) body.driverAccepted = updates.driverAccepted;
    if (updates.items !== undefined) body.itemIds = updates.items;
    if (updates.price !== undefined) body.price = updates.price;
    if (updates.serviceType !== undefined)
      body.serviceType = updates.serviceType as unknown as UpdateOrderRequest["serviceType"];
    if (updates.nextFirstTimePickupStep !== undefined) body.nextFirstTimePickupStep = updates.nextFirstTimePickupStep;
    if (updates.nextLocalDeliveryStep !== undefined) body.nextLocalDeliveryStep = updates.nextLocalDeliveryStep;
    if (updates.nextLocalPickupStep !== undefined) body.nextLocalPickupStep = updates.nextLocalPickupStep;
    if (updates.fulfillmentDate !== undefined) {
      const { earliest, latest } = parseFulfillmentDateTime(updates.fulfillmentDate, updates.fulfillmentTime);
      body.earliestFulfillment = earliest;
      body.latestFulfillment = latest;
    }
    if (updates.address) {
      body.address = {
        apartment: updates.address.apartment,
        country: updates.address.country,
        formattedAddress: updates.address.formatted_address,
        lat: updates.address.lat?.toString(),
        lng: updates.address.lng?.toString(),
        name: updates.address.name,
        placeId: updates.address.placeId,
        state: updates.address.state,
        street: updates.address.street,
        zipCode: updates.address.zipCode,
      };
    }
    if (updates.preferences) {
      body.preferences = {
        accommodation: updates.preferences.accommodation as unknown as NonNullable<
          UpdateOrderRequest["preferences"]
        >["accommodation"],
        details: updates.preferences.details,
        hotelName: updates.preferences.hotelName,
        instructions: updates.preferences.instruction as unknown as NonNullable<
          UpdateOrderRequest["preferences"]
        >["instructions"],
        reservationDetails: updates.preferences.reservationDetails,
        unitNumber: updates.preferences.unitNumber,
      };
    }
    await this.apiService.sendPatchRequest<UpdateOrderBody, void>(`${apiPath.order}/${id}`, body);
  }

  public async reassignUser(orderId: string, userId: string): Promise<void> {
    await this.apiService.sendPatchRequest<{ userId: string }, void>(`${apiPath.order}/${orderId}/user`, { userId });
  }

  public async addItemToOrder(orderId: string, itemId: string): Promise<Order> {
    const response = await this.apiService.sendPostRequest<undefined, OrderResponse>(
      `${apiPath.order}/${orderId}/items/${itemId}`,
      undefined,
    );
    return toOrder(response);
  }

  public async removeItemFromOrder(orderId: string, itemId: string): Promise<Order> {
    const response = await this.apiService.sendDeleteRequest<OrderResponse>(
      `${apiPath.order}/${orderId}/items/${itemId}`,
      undefined,
    );
    return toOrder(response);
  }

  public async delete(id: string): Promise<void> {
    await this.apiService.sendDeleteRequest<void>(`${apiPath.order}/${id}`, undefined);
  }

  public async search(searchTerm: string): Promise<Order[]> {
    const response = await this.apiService.sendGetRequest<ApiOrderDatePage, { fuzzySearchQuery: string }>(
      apiPath.order,
      { fuzzySearchQuery: searchTerm },
    );
    return response.orders.map((o) => toOrder(o as unknown as OrderResponse));
  }

  public async getAll(): Promise<Order[]> {
    const response = await this.apiService.sendGetRequest<OrdersForUserResponse>(apiPath.order, undefined);
    return response.orders.map(toOrder);
  }

  public async getByItemId(itemId: string): Promise<Order[]> {
    const response = await this.apiService.sendGetRequest<OrdersForUserResponse>(
      `${apiPath.order}/by-item/${itemId}`,
      undefined,
    );
    return response.orders.map(toOrder);
  }
}
